'use client'

import { useCommandState } from 'cmdk'
import { useCallback, useEffect, useMemo, useState } from 'react'

import Checkbox from '@/components/atomic/checkbox'
import { BodySubtext, BodyXS, FontWeight } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

import {
  Multiselect,
  MultiselectBadge,
  MultiselectBadgeList,
  MultiselectContent,
  MultiselectEmpty,
  MultiselectInput,
  MultiselectItem,
  MultiselectTrigger,
} from './base'

interface MultiSelectProps {
  value?: string[]
  onValueChange?: (value: string[]) => void
  defaultValue?: string[]
  options: {
    value: string
    label: string
    rightContent?: React.ReactNode
    subtext?: string
  }[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  wrapperClassName?: string
  label?: string
  disabled?: boolean
  size?: 'small' | 'large'
  slotProps?: {
    label?: string
    icon?: React.ReactNode
    side?: 'left' | 'right'
    showSeparator?: boolean
    separatorHeight?: string
    size?: 'small' | 'large'
    className?: string
  }
  error?: boolean
  /** When true, the popover will be rendered in a modal dialog, this will fix the issue preventing scrolling interaction */
  modal?: boolean
  /**
   * When true, search will filter by option labels instead of just values.
   * This is useful when you have options with descriptive labels that differ from their values.
   * @default false
   * @example
   * // With searchByLabel=true, searching "John" will find option with label "John Doe" and value "user_123"
   * // With searchByLabel=false, you'd need to search "user_123" to find the same option
   */
  searchByLabel?: boolean
  badgeClassName?: string
  /**
   * When true, displays a "Select all" option at the top of the options list.
   * @default false
   */
  showSelectAll?: boolean
  /**
   * When true, the trigger expands vertically to show all selected badges
   * instead of being fixed height with horizontal scrolling.
   * @default false
   */
  grow?: boolean
  /**
   * Callback fired when the popover open state changes.
   */
  onOpenChange?: (open: boolean) => void
}

/**
 * @deprecated Use the multi-select variant of `Select` from `@moment/design-system-ui/select` instead.
 */
export function MultiSelect({
  value,
  onValueChange,
  defaultValue,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  wrapperClassName,
  label,
  disabled = false,
  size = 'small',
  slotProps,
  error,
  modal = false,
  searchByLabel = false,
  badgeClassName,
  showSelectAll = false,
  grow = false,
  onOpenChange,
}: MultiSelectProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(value || [])

  useEffect(() => {
    setSelectedOptions(value || [])
  }, [value])

  const handleValueChange = useCallback(
    (newValue: string[]) => {
      setSelectedOptions(newValue)
      onValueChange?.(newValue)
    },
    [onValueChange],
  )

  const getLabelFromValue = (value: string) => {
    const option = options.find((o) => o.value === value)
    return option?.label || value
  }

  const filter = useCallback(
    (value: string, search: string) => {
      if (!searchByLabel) {
        // Default behavior: search by value only
        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
      }

      // Search by label when searchByLabel is true
      const option = options.find((o) => o.value === value)
      if (!option) return 0

      // Check if search term matches the label
      if (option.label.toLowerCase().includes(search.toLowerCase())) {
        return 1
      }

      // Also check the value as fallback
      if (value.toLowerCase().includes(search.toLowerCase())) {
        return 1
      }

      return 0
    },
    [options, searchByLabel],
  )

  return (
    <div className={wrapperClassName}>
      {label && (
        <BodyXS className="mb-1" color={'text-secondary'}>
          {label}
        </BodyXS>
      )}
      <Multiselect
        value={selectedOptions}
        onValueChange={handleValueChange}
        defaultValue={defaultValue}
        disabled={disabled}
        modal={modal}
        onOpenChange={onOpenChange}
      >
        <MultiselectTrigger
          className={cn(
            className,
            error &&
              '[&.error]:outline-hidden [&.error]:ring-2 [&.error]:border-1 [&.error]:border-moment-red-600 [&.error]:ring-moment-red-200 dark:[&.error]:border-moment-red-400 dark:[&.error]:ring-moment-red-900',
            error && 'error',
          )}
          placeholder={placeholder}
          grow={grow}
          slotProps={{ ...slotProps }}
          disabled={disabled}
          size={size}
        >
          <MultiselectBadgeList>
            {selectedOptions.map((option) => (
              <MultiselectBadge
                key={option}
                value={option}
                badgeClassName={badgeClassName}
              >
                {getLabelFromValue(option)}
              </MultiselectBadge>
            ))}
          </MultiselectBadgeList>
        </MultiselectTrigger>
        <MultiselectContent filter={filter}>
          <MultiselectInput
            placeholder={searchPlaceholder}
            disabled={disabled}
          />
          {showSelectAll && (
            <SelectAllRow
              options={options}
              selectedOptions={selectedOptions}
              filter={filter}
              disabled={disabled}
              onValueChange={handleValueChange}
            />
          )}
          {options.map((option) => (
            <MultiselectItem
              key={option.value}
              value={option.value}
              disabled={disabled}
              rightContent={option.rightContent}
              subtext={option.subtext}
            >
              <BodySubtext className="truncate">{option.label}</BodySubtext>
            </MultiselectItem>
          ))}
          <MultiselectEmpty>{emptyMessage}</MultiselectEmpty>
        </MultiselectContent>
      </Multiselect>
    </div>
  )
}

interface SelectAllRowProps {
  options: { value: string; label: string }[]
  selectedOptions: string[]
  filter: (value: string, search: string) => number
  disabled: boolean
  onValueChange: (newValue: string[]) => void
}

// Rendered inside <MultiselectContent> so it can read cmdk's search state
// and act on the currently visible options, preserving selections outside
// the active filter.
function SelectAllRow({
  options,
  selectedOptions,
  filter,
  disabled,
  onValueChange,
}: SelectAllRowProps) {
  const search = useCommandState((state) => state.search)

  const visibleValues = useMemo(() => {
    if (!search) return options.map((option) => option.value)
    return options
      .filter((option) => filter(option.value, search) > 0)
      .map((option) => option.value)
  }, [options, search, filter])

  const isAllSelected = useMemo(() => {
    if (visibleValues.length === 0) return false
    const selectedSet = new Set(selectedOptions)
    return visibleValues.every((v) => selectedSet.has(v))
  }, [visibleValues, selectedOptions])

  const handleToggle = useCallback(() => {
    if (isAllSelected) {
      const visibleSet = new Set(visibleValues)
      onValueChange(selectedOptions.filter((v) => !visibleSet.has(v)))
    } else {
      const merged = new Set([...selectedOptions, ...visibleValues])
      onValueChange(Array.from(merged))
    }
  }, [isAllSelected, visibleValues, selectedOptions, onValueChange])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle()
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: Checkbox primitive renders as <button>; nesting buttons is invalid HTML, so the row uses role="button".
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : handleToggle}
      onKeyDown={disabled ? undefined : handleKeyDown}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-surface-hover',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <Checkbox checked={isAllSelected} disabled={disabled} />
      <BodySubtext fontWeight={FontWeight.Medium} className="text-primary">
        Select all
      </BodySubtext>
    </div>
  )
}
